import {
  __export,
  __reExport,
  applyChange,
  assert,
  assertObject,
  assertString,
  compareValues,
  drainStreams,
  must,
  normalizeUndefined,
  throwInvalidType,
  unreachable,
  valuesEqual
} from "./chunk-SKR5JO2A.js";

// ../shared/src/valita.ts
var valita_exports = {};
__export(valita_exports, {
  assert: () => assert2,
  deepPartial: () => deepPartial,
  instanceOfAbstractType: () => instanceOfAbstractType,
  is: () => is,
  literalUnion: () => literalUnion,
  parse: () => parse,
  readonly: () => readonly,
  readonlyArray: () => readonlyArray,
  readonlyObject: () => readonlyObject,
  readonlyRecord: () => readonlyRecord,
  test: () => test,
  testOptional: () => testOptional
});
__reExport(valita_exports, valita_star);
import * as v from "@badrap/valita";
import * as valita_star from "@badrap/valita";
function toDisplay(value) {
  switch (typeof value) {
    case "string":
    case "number":
    case "boolean":
      return JSON.stringify(value);
    case "undefined":
      return "undefined";
    case "bigint":
      return value.toString() + "n";
    default:
      if (value === null) {
        return "null";
      }
      if (Array.isArray(value)) {
        return "array";
      }
      return typeof value;
  }
}
function toDisplayAtPath(v2, path2) {
  if (!path2?.length) {
    return toDisplay(v2);
  }
  let cur = v2;
  for (const p of path2) {
    cur = cur[p];
  }
  return toDisplay(cur);
}
function displayList(word, expected, toDisplay2 = (x) => String(x)) {
  if (expected.length === 1) {
    return toDisplay2(expected[0]);
  }
  const suffix = `${toDisplay2(
    expected[expected.length - 2]
  )} ${word} ${toDisplay2(expected[expected.length - 1])}`;
  if (expected.length === 2) {
    return suffix;
  }
  return `${expected.slice(0, -2).map(toDisplay2).join(", ")}, ${suffix}`;
}
function getMessage(err2, v2, schema, mode) {
  const firstIssue = err2.issues[0];
  const { path: path2 } = firstIssue;
  const atPath = path2?.length ? ` at ${path2.join(".")}` : "";
  switch (firstIssue.code) {
    case "invalid_type":
      return `Expected ${displayList(
        "or",
        firstIssue.expected
      )}${atPath}. Got ${toDisplayAtPath(v2, path2)}`;
    case "missing_value": {
      const atPath2 = path2 && path2.length > 1 ? ` at ${path2.slice(0, -1).join(".")}` : "";
      if (firstIssue.path?.length) {
        return `Missing property ${firstIssue.path.at(-1)}${atPath2}`;
      }
      return `TODO Unknown missing property${atPath2}`;
    }
    case "invalid_literal":
      return `Expected literal value ${displayList(
        "or",
        firstIssue.expected,
        toDisplay
      )}${atPath} Got ${toDisplayAtPath(v2, path2)}`;
    case "invalid_length": {
      return `Expected array with length ${firstIssue.minLength === firstIssue.maxLength ? firstIssue.minLength : `between ${firstIssue.minLength} and ${firstIssue.maxLength}`}${atPath}. Got array with length ${v2.length}`;
    }
    case "unrecognized_keys":
      if (firstIssue.keys.length === 1) {
        return `Unexpected property ${firstIssue.keys[0]}${atPath}`;
      }
      return `Unexpected properties ${displayList(
        "and",
        firstIssue.keys
      )}${atPath}`;
    case "invalid_union":
      return schema.name === "union" ? getDeepestUnionParseError(v2, schema, mode ?? "strict") : `Invalid union value${atPath}`;
    case "custom_error": {
      const { error } = firstIssue;
      const message = !error ? "unknown" : typeof error === "string" ? error : error.message ?? "unknown";
      return `${message}${atPath}. Got ${toDisplayAtPath(v2, path2)}`;
    }
  }
}
function getDeepestUnionParseError(value, schema, mode) {
  const failures = [];
  for (const type of schema.options) {
    const r = type.try(value, { mode });
    if (!r.ok) {
      failures.push({ type, err: r });
    }
  }
  if (failures.length) {
    failures.sort(pathCmp);
    if (failures.length === 1 || pathCmp(failures[0], failures[1]) < 0) {
      return getMessage(failures[0].err, value, failures[0].type, mode);
    }
  }
  try {
    const str = JSON.stringify(value);
    return `Invalid union value: ${str}`;
  } catch (e) {
    return `Invalid union value`;
  }
}
function pathCmp(a, b) {
  const aPath = a.err.issues[0].path;
  const bPath = b.err.issues[0].path;
  if (aPath.length !== bPath.length) {
    return bPath.length - aPath.length;
  }
  for (let i = 0; i < aPath.length; i++) {
    if (bPath[i] > aPath[i]) {
      return -1;
    }
    if (bPath[i] < aPath[i]) {
      return 1;
    }
  }
  return 0;
}
function parse(value, schema, mode) {
  const res = test(value, schema, mode);
  if (!res.ok) {
    throw new TypeError(res.error);
  }
  return res.value;
}
function is(value, schema, mode) {
  return test(value, schema, mode).ok;
}
function assert2(value, schema, mode) {
  parse(value, schema, mode);
}
function test(value, schema, mode) {
  const res = schema.try(value, mode ? { mode } : void 0);
  if (!res.ok) {
    return {
      ok: false,
      error: getMessage(res, value, schema, mode)
    };
  }
  return res;
}
function testOptional(value, schema, mode) {
  let flags = 1;
  if (mode === "passthrough") {
    flags = 0;
  } else if (mode === "strip") {
    flags = 2;
  }
  const res = schema.func(value, flags);
  if (res === void 0) {
    return { ok: true, value };
  } else if (res.ok) {
    return res;
  }
  const err2 = new v.ValitaError(res);
  return { ok: false, error: getMessage(err2, value, schema, mode) };
}
function readonly(t) {
  return t;
}
function readonlyObject(t) {
  return v.object(t);
}
function readonlyArray(t) {
  return v.array(t);
}
function readonlyRecord(t) {
  return v.record(t);
}
var AbstractType = Object.getPrototypeOf(
  Object.getPrototypeOf(v.string().optional())
).constructor;
function instanceOfAbstractType(obj) {
  return obj instanceof AbstractType;
}
function deepPartial(s) {
  const shape = {};
  for (const [key, type] of Object.entries(s.shape)) {
    if (type.name === "object") {
      shape[key] = deepPartial(type).optional();
    } else {
      shape[key] = type.optional();
    }
  }
  return v.object(shape);
}
function literalUnion(...literals) {
  return v.union(...literals.map(v.literal));
}

// ../zql/src/query/ttl.ts
var DEFAULT_TTL = "none";
var multiplier = {
  s: 1e3,
  m: 60 * 1e3,
  h: 60 * 60 * 1e3,
  d: 24 * 60 * 60 * 1e3,
  y: 365 * 24 * 60 * 60 * 1e3
};
function parseTTL(ttl) {
  if (typeof ttl === "number") {
    return Number.isNaN(ttl) ? 0 : !Number.isFinite(ttl) || ttl < 0 ? -1 : ttl;
  }
  if (ttl === "none") {
    return 0;
  }
  if (ttl === "forever") {
    return -1;
  }
  const multi = multiplier[ttl[ttl.length - 1]];
  return Number(ttl.slice(0, -1)) * multi;
}
function compareTTL(a, b) {
  const ap = parseTTL(a);
  const bp = parseTTL(b);
  if (ap === -1 && bp !== -1) {
    return 1;
  }
  if (ap !== -1 && bp === -1) {
    return -1;
  }
  return ap - bp;
}
function normalizeTTL(ttl) {
  if (typeof ttl === "string") {
    return ttl;
  }
  if (ttl < 0) {
    return "forever";
  }
  if (ttl === 0) {
    return "none";
  }
  let shortest = ttl.toString();
  const lengthOfNumber = shortest.length;
  for (const unit of ["y", "d", "h", "m", "s"]) {
    const multi = multiplier[unit];
    const value = ttl / multi;
    const candidate = `${value}${unit}`;
    if (candidate.length < shortest.length) {
      shortest = candidate;
    }
  }
  return shortest.length < lengthOfNumber ? shortest : ttl;
}

// ../shared/src/has-own.ts
var { hasOwn } = Object;

// ../shared/src/hash.ts
import { xxHash32 } from "js-xxhash";
var h64 = (s) => hash(s, 2);
var h128 = (s) => hash(s, 4);
function hash(str, words) {
  let hash2 = 0n;
  for (let i = 0; i < words; i++) {
    hash2 = (hash2 << 32n) + BigInt(xxHash32(str, i));
  }
  return hash2;
}

// ../zero-protocol/src/ast.ts
import { compareUTF8 } from "compare-utf8";

// ../shared/src/arrays.ts
function defined(arr) {
  let i = arr.findIndex((x) => x === void 0);
  if (i < 0) {
    return arr;
  }
  const defined2 = arr.slice(0, i);
  for (i++; i < arr.length; i++) {
    const x = arr[i];
    if (x !== void 0) {
      defined2.push(x);
    }
  }
  return defined2;
}
function areEqual(arr1, arr2) {
  return arr1.length === arr2.length && arr1.every((e, i) => e === arr2[i]);
}

// ../shared/src/json-schema.ts
import * as valita from "@badrap/valita";

// ../shared/src/config.ts
var isProd = process.env.NODE_ENV === "production";

// ../shared/src/json.ts
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  switch (typeof a) {
    case "boolean":
    case "number":
    case "string":
      return false;
  }
  a = a;
  if (Array.isArray(a)) {
    if (!Array.isArray(b)) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }
  if (a === null || b === null) {
    return false;
  }
  if (Array.isArray(b)) {
    return false;
  }
  a = a;
  b = b;
  let aSize = 0;
  for (const key in a) {
    if (hasOwn(a, key)) {
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
      aSize++;
    }
  }
  let bSize = 0;
  for (const key in b) {
    if (hasOwn(b, key)) {
      bSize++;
    }
  }
  return aSize === bSize;
}
function assertJSONValue(v2) {
  if (isProd) {
    return;
  }
  switch (typeof v2) {
    case "boolean":
    case "number":
    case "string":
      return;
    case "object":
      if (v2 === null) {
        return;
      }
      if (Array.isArray(v2)) {
        return assertJSONArray(v2);
      }
      return assertObjectIsJSONObject(v2);
  }
  throwInvalidType(v2, "JSON value");
}
function assertJSONObject(v2) {
  assertObject(v2);
  assertObjectIsJSONObject(v2);
}
function assertObjectIsJSONObject(v2) {
  for (const k in v2) {
    if (hasOwn(v2, k)) {
      const value = v2[k];
      if (value !== void 0) {
        assertJSONValue(value);
      }
    }
  }
}
function assertJSONArray(v2) {
  for (const item of v2) {
    assertJSONValue(item);
  }
}
function isJSONValue(v2, path2) {
  switch (typeof v2) {
    case "boolean":
    case "number":
    case "string":
      return true;
    case "object":
      if (v2 === null) {
        return true;
      }
      if (Array.isArray(v2)) {
        return isJSONArray(v2, path2);
      }
      return objectIsJSONObject(v2, path2);
  }
  return false;
}
function isJSONObject(v2, path2) {
  if (typeof v2 !== "object" || v2 === null) {
    return false;
  }
  return objectIsJSONObject(v2, path2);
}
function objectIsJSONObject(v2, path2) {
  for (const k in v2) {
    if (hasOwn(v2, k)) {
      path2.push(k);
      const value = v2[k];
      if (value !== void 0 && !isJSONValue(value, path2)) {
        return false;
      }
      path2.pop();
    }
  }
  return true;
}
function isJSONArray(v2, path2) {
  for (let i = 0; i < v2.length; i++) {
    path2.push(i);
    if (!isJSONValue(v2[i], path2)) {
      return false;
    }
    path2.pop();
  }
  return true;
}

// ../shared/src/json-schema.ts
var path = [];
var jsonSchema = valita_exports.unknown().chain((v2) => {
  if (isProd) {
    return valita.ok(v2);
  }
  const rv = isJSONValue(v2, path) ? valita.ok(v2) : valita.err({
    message: `Not a JSON value`,
    path: path.slice()
  });
  path.length = 0;
  return rv;
});
var jsonObjectSchema = valita_exports.unknown().chain((v2) => {
  if (isProd) {
    return valita.ok(v2);
  }
  const rv = isJSONObject(v2, path) ? valita.ok(v2) : valita.err({
    message: `Not a JSON object`,
    path: path.slice()
  });
  path.length = 0;
  return rv;
});

// ../zero-protocol/src/data.ts
var valueSchema = valita_exports.union(jsonSchema, valita_exports.undefined());
var rowSchema = readonlyRecord(valueSchema);

// ../zero-protocol/src/ast.ts
var selectorSchema = valita_exports.string();
var toStaticParam = Symbol();
var orderingElementSchema = readonly(
  valita_exports.tuple([selectorSchema, literalUnion("asc", "desc")])
);
var orderingSchema = readonlyArray(orderingElementSchema);
var primitiveSchema = valita_exports.union(
  valita_exports.string(),
  valita_exports.number(),
  valita_exports.boolean(),
  valita_exports.null()
);
var equalityOpsSchema = literalUnion("=", "!=", "IS", "IS NOT");
var orderOpsSchema = literalUnion("<", ">", "<=", ">=");
var likeOpsSchema = literalUnion(
  "LIKE",
  "NOT LIKE",
  "ILIKE",
  "NOT ILIKE"
);
var inOpsSchema = literalUnion("IN", "NOT IN");
var simpleOperatorSchema = valita_exports.union(
  equalityOpsSchema,
  orderOpsSchema,
  likeOpsSchema,
  inOpsSchema
);
var literalReferenceSchema = readonlyObject({
  type: valita_exports.literal("literal"),
  value: valita_exports.union(
    valita_exports.string(),
    valita_exports.number(),
    valita_exports.boolean(),
    valita_exports.null(),
    readonlyArray(valita_exports.union(valita_exports.string(), valita_exports.number(), valita_exports.boolean()))
  )
});
var columnReferenceSchema = readonlyObject({
  type: valita_exports.literal("column"),
  name: valita_exports.string()
});
var parameterReferenceSchema = readonlyObject({
  type: valita_exports.literal("static"),
  // The "namespace" of the injected parameter.
  // Write authorization will send the value of a row
  // prior to the mutation being run (preMutationRow).
  // Read and write authorization will both send the
  // current authentication data (authData).
  anchor: literalUnion("authData", "preMutationRow"),
  field: valita_exports.union(valita_exports.string(), valita_exports.array(valita_exports.string()))
});
var conditionValueSchema = valita_exports.union(
  literalReferenceSchema,
  columnReferenceSchema,
  parameterReferenceSchema
);
var simpleConditionSchema = readonlyObject({
  type: valita_exports.literal("simple"),
  op: simpleOperatorSchema,
  left: conditionValueSchema,
  right: valita_exports.union(parameterReferenceSchema, literalReferenceSchema)
});
var correlatedSubqueryConditionOperatorSchema = literalUnion("EXISTS", "NOT EXISTS");
var correlatedSubqueryConditionSchema = readonlyObject({
  type: valita_exports.literal("correlatedSubquery"),
  related: valita_exports.lazy(() => correlatedSubquerySchema),
  op: correlatedSubqueryConditionOperatorSchema
});
var conditionSchema = valita_exports.union(
  simpleConditionSchema,
  valita_exports.lazy(() => conjunctionSchema),
  valita_exports.lazy(() => disjunctionSchema),
  correlatedSubqueryConditionSchema
);
var conjunctionSchema = readonlyObject({
  type: valita_exports.literal("and"),
  conditions: readonlyArray(conditionSchema)
});
var disjunctionSchema = readonlyObject({
  type: valita_exports.literal("or"),
  conditions: readonlyArray(conditionSchema)
});
function mustCompoundKey(field) {
  assert(Array.isArray(field) && field.length >= 1);
  return field;
}
var compoundKeySchema = readonly(
  valita_exports.tuple([valita_exports.string()]).concat(valita_exports.array(valita_exports.string()))
);
var correlationSchema = readonlyObject({
  parentField: compoundKeySchema,
  childField: compoundKeySchema
});
var correlatedSubquerySchemaOmitSubquery = readonlyObject({
  correlation: correlationSchema,
  hidden: valita_exports.boolean().optional(),
  system: literalUnion("permissions", "client", "test").optional()
});
var correlatedSubquerySchema = correlatedSubquerySchemaOmitSubquery.extend({
  subquery: valita_exports.lazy(() => astSchema)
});
var astSchema = readonlyObject({
  schema: valita_exports.string().optional(),
  table: valita_exports.string(),
  alias: valita_exports.string().optional(),
  where: conditionSchema.optional(),
  related: readonlyArray(correlatedSubquerySchema).optional(),
  limit: valita_exports.number().optional(),
  orderBy: orderingSchema.optional(),
  start: valita_exports.object({
    row: rowSchema,
    exclusive: valita_exports.boolean()
  }).optional()
});
function transformAST(ast, transform) {
  const { tableName, columnName } = transform;
  const colName = (c) => columnName(ast.table, c);
  const key = (table, k) => {
    const serverKey = k.map((col) => columnName(table, col));
    return mustCompoundKey(serverKey);
  };
  const where = ast.where ? transform.where(ast.where) : void 0;
  const transformed = {
    schema: ast.schema,
    table: tableName(ast.table),
    alias: ast.alias,
    where: where ? transformWhere(where, ast.table, transform) : void 0,
    related: ast.related ? transform.related(
      ast.related.map(
        (r) => ({
          correlation: {
            parentField: key(ast.table, r.correlation.parentField),
            childField: key(r.subquery.table, r.correlation.childField)
          },
          hidden: r.hidden,
          subquery: transformAST(r.subquery, transform),
          system: r.system
        })
      )
    ) : void 0,
    start: ast.start ? {
      ...ast.start,
      row: Object.fromEntries(
        Object.entries(ast.start.row).map(([col, val]) => [
          colName(col),
          val
        ])
      )
    } : void 0,
    limit: ast.limit,
    orderBy: ast.orderBy?.map(([col, dir]) => [colName(col), dir])
  };
  return transformed;
}
function transformWhere(where, table, transform) {
  const { columnName } = transform;
  const condValue = (c) => c.type !== "column" ? c : { ...c, name: columnName(table, c.name) };
  const key = (table2, k) => {
    const serverKey = k.map((col) => columnName(table2, col));
    return mustCompoundKey(serverKey);
  };
  if (where.type === "simple") {
    return { ...where, left: condValue(where.left) };
  } else if (where.type === "correlatedSubquery") {
    const { correlation, subquery } = where.related;
    return {
      ...where,
      related: {
        ...where.related,
        correlation: {
          parentField: key(table, correlation.parentField),
          childField: key(subquery.table, correlation.childField)
        },
        subquery: transformAST(subquery, transform)
      }
    };
  }
  return {
    type: where.type,
    conditions: transform.conditions(
      where.conditions.map((c) => transformWhere(c, table, transform))
    )
  };
}
var normalizeCache = /* @__PURE__ */ new WeakMap();
var NORMALIZE_TRANSFORM = {
  tableName: (t) => t,
  columnName: (_, c) => c,
  related: sortedRelated,
  where: flattened,
  conditions: (c) => c.sort(cmpCondition)
};
function normalizeAST(ast) {
  let normalized = normalizeCache.get(ast);
  if (!normalized) {
    normalized = transformAST(ast, NORMALIZE_TRANSFORM);
    normalizeCache.set(ast, normalized);
  }
  return normalized;
}
function mapAST(ast, mapper) {
  return transformAST(ast, {
    tableName: (table) => mapper.tableName(table),
    columnName: (table, col) => mapper.columnName(table, col),
    related: (r) => r,
    where: (w) => w,
    conditions: (c) => c
  });
}
function mapCondition(cond, table, mapper) {
  return transformWhere(cond, table, {
    tableName: (table2) => mapper.tableName(table2),
    columnName: (table2, col) => mapper.columnName(table2, col),
    related: (r) => r,
    where: (w) => w,
    conditions: (c) => c
  });
}
function sortedRelated(related) {
  return related.sort(cmpRelated);
}
function cmpCondition(a, b) {
  if (a.type === "simple") {
    if (b.type !== "simple") {
      return -1;
    }
    return compareValuePosition(a.left, b.left) || compareUTF8MaybeNull(a.op, b.op) || compareValuePosition(a.right, b.right);
  }
  if (b.type === "simple") {
    return 1;
  }
  if (a.type === "correlatedSubquery") {
    if (b.type !== "correlatedSubquery") {
      return -1;
    }
    return cmpRelated(a.related, b.related) || compareUTF8MaybeNull(a.op, b.op);
  }
  if (b.type === "correlatedSubquery") {
    return -1;
  }
  const val = compareUTF8MaybeNull(a.type, b.type);
  if (val !== 0) {
    return val;
  }
  for (let l = 0, r = 0; l < a.conditions.length && r < b.conditions.length; l++, r++) {
    const val2 = cmpCondition(a.conditions[l], b.conditions[r]);
    if (val2 !== 0) {
      return val2;
    }
  }
  return a.conditions.length - b.conditions.length;
}
function compareValuePosition(a, b) {
  if (a.type !== b.type) {
    return compareUTF8(a.type, b.type);
  }
  switch (a.type) {
    case "literal":
      assert(b.type === "literal");
      return compareUTF8(String(a.value), String(b.value));
    case "column":
      assert(b.type === "column");
      return compareUTF8(a.name, b.name);
    case "static":
      throw new Error(
        "Static parameters should be resolved before normalization"
      );
  }
}
function cmpRelated(a, b) {
  return compareUTF8(must(a.subquery.alias), must(b.subquery.alias));
}
function flattened(cond) {
  if (cond.type === "simple" || cond.type === "correlatedSubquery") {
    return cond;
  }
  const conditions = defined(
    cond.conditions.flatMap(
      (c) => c.type === cond.type ? c.conditions.map((c2) => flattened(c2)) : flattened(c)
    )
  );
  switch (conditions.length) {
    case 0:
      return void 0;
    case 1:
      return conditions[0];
    default:
      return {
        type: cond.type,
        conditions
      };
  }
}
function compareUTF8MaybeNull(a, b) {
  if (a !== null && b !== null) {
    return compareUTF8(a, b);
  }
  if (b !== null) {
    return -1;
  }
  if (a !== null) {
    return 1;
  }
  return 0;
}

// ../zero-schema/src/table-schema.ts
function isOneHop(r) {
  return r.length === 1;
}
function isTwoHop(r) {
  return r.length === 2;
}

// ../zql/src/query/query-impl.ts
import { resolver } from "@rocicorp/resolver";

// ../zero-protocol/src/ast-hash.ts
var hashCache = /* @__PURE__ */ new WeakMap();
function hashOfAST(ast) {
  const normalized = normalizeAST(ast);
  const cached = hashCache.get(normalized);
  if (cached) {
    return cached;
  }
  const hash2 = h64(JSON.stringify(normalized)).toString(36);
  hashCache.set(normalized, hash2);
  return hash2;
}

// ../zql/src/ivm/filter-operators.ts
var throwFilterOutput = {
  push(_change) {
    throw new Error("Output not set");
  },
  filter(_node, _cleanup) {
    throw new Error("Output not set");
  }
};
var FilterStart = class {
  #input;
  #output = throwFilterOutput;
  constructor(input) {
    this.#input = input;
    input.setOutput(this);
  }
  setFilterOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  push(change) {
    this.#output.push(change);
  }
  *fetch(req) {
    for (const node of this.#input.fetch(req)) {
      if (this.#output.filter(node, false)) {
        yield node;
      }
    }
  }
  *cleanup(req) {
    for (const node of this.#input.cleanup(req)) {
      if (this.#output.filter(node, true)) {
        yield node;
      } else {
        drainStreams(node);
      }
    }
  }
};
var FilterEnd = class {
  #start;
  #input;
  #output = throwFilterOutput;
  constructor(start, input) {
    this.#start = start;
    this.#input = input;
    input.setFilterOutput(this);
  }
  *fetch(req) {
    for (const node of this.#start.fetch(req)) {
      yield node;
    }
  }
  *cleanup(req) {
    for (const node of this.#start.cleanup(req)) {
      yield node;
    }
  }
  filter(_node, _cleanup) {
    return true;
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  push(change) {
    this.#output.push(change);
  }
};
function buildFilterPipeline(input, pipeline) {
  const filterStart = new FilterStart(input);
  return new FilterEnd(filterStart, pipeline(filterStart));
}

// ../zql/src/ivm/operator.ts
var throwOutput = {
  push(_change) {
    throw new Error("Output not set");
  }
};

// ../zql/src/ivm/stream.ts
function* take(stream, limit) {
  if (limit < 1) {
    return;
  }
  let count = 0;
  for (const v2 of stream) {
    yield v2;
    if (++count === limit) {
      break;
    }
  }
}
function first(stream) {
  const it = stream[Symbol.iterator]();
  const { value } = it.next();
  it.return?.();
  return value;
}

// ../zql/src/ivm/exists.ts
var Exists = class {
  #input;
  #relationshipName;
  #storage;
  #not;
  #parentJoinKey;
  #noSizeReuse;
  #output = throwFilterOutput;
  /**
   * This instance variable is `true` when this operator is processing a `push`,
   * and is used to disable reuse of cached sizes across rows with the
   * same parent join key value.
   * This is necessary because during a push relationships can be inconsistent
   * due to push communicating changes (which may change multiple Nodes) one
   * Node at a time.
   */
  #inPush = false;
  constructor(input, storage, relationshipName, parentJoinKey, type) {
    this.#input = input;
    this.#relationshipName = relationshipName;
    this.#input.setFilterOutput(this);
    this.#storage = storage;
    assert(
      this.#input.getSchema().relationships[relationshipName],
      `Input schema missing ${relationshipName}`
    );
    this.#not = type === "NOT EXISTS";
    this.#parentJoinKey = parentJoinKey;
    this.#noSizeReuse = areEqual(
      parentJoinKey,
      this.#input.getSchema().primaryKey
    );
  }
  setFilterOutput(output) {
    this.#output = output;
  }
  filter(node, cleanup) {
    const result = this.#filter(node) && this.#output.filter(node, cleanup);
    if (cleanup) {
      this.#delSize(node);
    }
    return result;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  push(change) {
    assert(!this.#inPush, "Unexpected re-entrancy");
    this.#inPush = true;
    try {
      switch (change.type) {
        // add, remove and edit cannot change the size of the
        // this.#relationshipName relationship, so simply #pushWithFilter
        case "add":
        case "edit": {
          this.#pushWithFilter(change);
          return;
        }
        case "remove": {
          const size = this.#getSize(change.node);
          if (size === void 0) {
            return;
          }
          this.#pushWithFilter(change, size);
          this.#delSize(change.node);
          return;
        }
        case "child":
          if (change.child.relationshipName !== this.#relationshipName || change.child.change.type === "edit" || change.child.change.type === "child") {
            this.#pushWithFilter(change);
            return;
          }
          switch (change.child.change.type) {
            case "add": {
              let size = this.#getSize(change.node);
              if (size !== void 0) {
                size++;
                this.#setSize(change.node, size);
              } else {
                size = this.#fetchSize(change.node);
              }
              if (size === 1) {
                if (this.#not) {
                  this.#output.push({
                    type: "remove",
                    node: {
                      row: change.node.row,
                      relationships: {
                        ...change.node.relationships,
                        [this.#relationshipName]: () => []
                      }
                    }
                  });
                } else {
                  this.#output.push({
                    type: "add",
                    node: change.node
                  });
                }
              } else {
                this.#pushWithFilter(change, size);
              }
              return;
            }
            case "remove": {
              let size = this.#getSize(change.node);
              if (size !== void 0) {
                assert(size > 0);
                size--;
                this.#setSize(change.node, size);
              } else {
                size = this.#fetchSize(change.node);
              }
              if (size === 0) {
                if (this.#not) {
                  this.#output.push({
                    type: "add",
                    node: change.node
                  });
                } else {
                  this.#output.push({
                    type: "remove",
                    node: {
                      row: change.node.row,
                      relationships: {
                        ...change.node.relationships,
                        [this.#relationshipName]: () => [
                          change.child.change.node
                        ]
                      }
                    }
                  });
                }
              } else {
                this.#pushWithFilter(change, size);
              }
              return;
            }
          }
          return;
        default:
          unreachable(change);
      }
    } finally {
      this.#inPush = false;
    }
  }
  /**
   * Returns whether or not the node's this.#relationshipName
   * relationship passes the exist/not exists filter condition.
   * If the optional `size` is passed it is used.
   * Otherwise, if there is a stored size for the row it is used.
   * Otherwise the size is computed by streaming the node's
   * relationship with this.#relationshipName (this computed size is also
   * stored).
   */
  #filter(node, size) {
    const exists = (size ?? this.#getOrFetchSize(node)) > 0;
    return this.#not ? !exists : exists;
  }
  /**
   * Pushes a change if this.#filter is true for its row.
   */
  #pushWithFilter(change, size) {
    if (this.#filter(change.node, size)) {
      this.#output.push(change);
    }
  }
  #getSize(node) {
    return this.#storage.get(this.#makeSizeStorageKey(node));
  }
  #setSize(node, size) {
    this.#storage.set(this.#makeSizeStorageKey(node), size);
  }
  #delSize(node) {
    this.#storage.del(this.#makeSizeStorageKey(node));
  }
  #getOrFetchSize(node) {
    const size = this.#getSize(node);
    if (size !== void 0) {
      return size;
    }
    return this.#fetchSize(node);
  }
  #fetchSize(node) {
    if (!this.#noSizeReuse && !this.#inPush) {
      const cachedSizeEntry = first(
        this.#storage.scan({
          prefix: this.#makeSizeStorageKeyPrefix(node)
        })
      );
      if (cachedSizeEntry !== void 0) {
        this.#setSize(node, cachedSizeEntry[1]);
        return cachedSizeEntry[1];
      }
    }
    const relationship = node.relationships[this.#relationshipName];
    assert(relationship);
    let size = 0;
    for (const _relatedNode of relationship()) {
      size++;
    }
    this.#setSize(node, size);
    return size;
  }
  #makeSizeStorageKeyPrefix(node) {
    return `row/${this.#noSizeReuse ? "" : JSON.stringify(this.#getKeyValues(node, this.#parentJoinKey))}/`;
  }
  #makeSizeStorageKey(node) {
    return `${this.#makeSizeStorageKeyPrefix(node)}${JSON.stringify(
      this.#getKeyValues(node, this.#input.getSchema().primaryKey)
    )}`;
  }
  #getKeyValues(node, def) {
    const values = [];
    for (const key of def) {
      values.push(normalizeUndefined(node.row[key]));
    }
    return values;
  }
};

// ../zql/src/ivm/fan-in.ts
var FanIn = class {
  #inputs;
  #schema;
  #output = throwFilterOutput;
  #accumulatedPushes = [];
  constructor(fanOut, inputs) {
    this.#inputs = inputs;
    this.#schema = fanOut.getSchema();
    for (const input of inputs) {
      input.setFilterOutput(this);
      assert(this.#schema === input.getSchema(), `Schema mismatch in fan-in`);
    }
  }
  setFilterOutput(output) {
    this.#output = output;
  }
  destroy() {
    for (const input of this.#inputs) {
      input.destroy();
    }
  }
  getSchema() {
    return this.#schema;
  }
  filter(node, cleanup) {
    return this.#output.filter(node, cleanup);
  }
  push(change) {
    this.#accumulatedPushes.push(change);
  }
  fanOutDonePushingToAllBranches(fanOutChangeType) {
    if (this.#inputs.length === 0) {
      assert(
        this.#accumulatedPushes.length === 0,
        "If there are no inputs then fan-in should not receive any pushes."
      );
      return;
    }
    if (this.#accumulatedPushes.length === 0) {
      return;
    }
    const candidatesToPush = /* @__PURE__ */ new Map();
    for (const change of this.#accumulatedPushes) {
      if (fanOutChangeType === "child" && change.type !== "child") {
        assert(
          candidatesToPush.has(change.type) === false,
          () => `Fan-in:child expected at most one ${change.type} when fan-out is of type child`
        );
      }
      candidatesToPush.set(change.type, change);
    }
    this.#accumulatedPushes = [];
    const types = [...candidatesToPush.keys()];
    switch (fanOutChangeType) {
      case "remove":
        assert(
          types.length === 1 && types[0] === "remove",
          "Fan-in:remove expected all removes"
        );
        this.#output.push(must(candidatesToPush.get("remove")));
        return;
      case "add":
        assert(
          types.length === 1 && types[0] === "add",
          "Fan-in:add expected all adds"
        );
        this.#output.push(must(candidatesToPush.get("add")));
        return;
      case "edit": {
        assert(
          types.every(
            (type) => type === "add" || type === "remove" || type === "edit"
          ),
          "Fan-in:edit expected all adds, removes, or edits"
        );
        const addChange = candidatesToPush.get("add");
        const removeChange = candidatesToPush.get("remove");
        const editChange = candidatesToPush.get("edit");
        if (editChange) {
          this.#output.push(editChange);
          return;
        }
        if (addChange && removeChange) {
          this.#output.push({
            type: "edit",
            node: addChange.node,
            oldNode: removeChange.node
          });
          return;
        }
        this.#output.push(must(addChange ?? removeChange));
        return;
      }
      case "child": {
        assert(
          types.every(
            (type) => type === "add" || // exists can change child to add or remove
            type === "remove" || // exists can change child to add or remove
            type === "child"
            // other operators may preserve the child change
          ),
          "Fan-in:child expected all adds, removes, or children"
        );
        assert(
          types.length <= 2,
          "Fan-in:child expected at most 2 types on a child change from fan-out"
        );
        const childChange = candidatesToPush.get("child");
        if (childChange) {
          this.#output.push(childChange);
          return;
        }
        const addChange = candidatesToPush.get("add");
        const removeChange = candidatesToPush.get("remove");
        assert(
          addChange === void 0 || removeChange === void 0,
          "Fan-in:child expected either add or remove, not both"
        );
        this.#output.push(must(addChange ?? removeChange));
        return;
      }
      default:
        fanOutChangeType;
    }
  }
};

// ../zql/src/ivm/fan-out.ts
var FanOut = class {
  #input;
  #outputs = [];
  #fanIn;
  #destroyCount = 0;
  constructor(input) {
    this.#input = input;
    input.setFilterOutput(this);
  }
  setFanIn(fanIn) {
    this.#fanIn = fanIn;
  }
  setFilterOutput(output) {
    this.#outputs.push(output);
  }
  destroy() {
    if (this.#destroyCount < this.#outputs.length) {
      if (this.#destroyCount === 0) {
        this.#input.destroy();
      }
      ++this.#destroyCount;
    } else {
      throw new Error("FanOut already destroyed once for each output");
    }
  }
  getSchema() {
    return this.#input.getSchema();
  }
  filter(node, cleanup) {
    let result = false;
    for (const output of this.#outputs) {
      result = output.filter(node, cleanup) || result;
      if (!cleanup && result) {
        return true;
      }
    }
    return result;
  }
  push(change) {
    for (const out of this.#outputs) {
      out.push(change);
    }
    must(
      this.#fanIn,
      "fan-out must have a corresponding fan-in set!"
    ).fanOutDonePushingToAllBranches(change.type);
  }
};

// ../zql/src/ivm/maybe-split-and-push-edit-change.ts
function maybeSplitAndPushEditChange(change, predicate, output) {
  const oldWasPresent = predicate(change.oldNode.row);
  const newIsPresent = predicate(change.node.row);
  if (oldWasPresent && newIsPresent) {
    output.push(change);
  } else if (oldWasPresent && !newIsPresent) {
    output.push({
      type: "remove",
      node: change.oldNode
    });
  } else if (!oldWasPresent && newIsPresent) {
    output.push({
      type: "add",
      node: change.node
    });
  }
}

// ../zql/src/ivm/filter-push.ts
function filterPush(change, output, predicate) {
  if (!predicate) {
    output.push(change);
    return;
  }
  switch (change.type) {
    case "add":
    case "remove":
      if (predicate(change.node.row)) {
        output.push(change);
      }
      break;
    case "child":
      if (predicate(change.node.row)) {
        output.push(change);
      }
      break;
    case "edit":
      maybeSplitAndPushEditChange(change, predicate, output);
      break;
    default:
      unreachable(change);
  }
}

// ../zql/src/ivm/filter.ts
var Filter = class {
  #input;
  #predicate;
  #output = throwFilterOutput;
  constructor(input, predicate) {
    this.#input = input;
    this.#predicate = predicate;
    input.setFilterOutput(this);
  }
  filter(node, cleanup) {
    return this.#predicate(node.row) && this.#output.filter(node, cleanup);
  }
  setFilterOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  getSchema() {
    return this.#input.getSchema();
  }
  push(change) {
    filterPush(change, this.#output, this.#predicate);
  }
};

// ../zql/src/ivm/join.ts
var Join = class {
  #parent;
  #child;
  #storage;
  #parentKey;
  #childKey;
  #relationshipName;
  #schema;
  #output = throwOutput;
  #inprogressChildChange;
  constructor({
    parent,
    child,
    storage,
    parentKey,
    childKey,
    relationshipName,
    hidden,
    system
  }) {
    assert(parent !== child, "Parent and child must be different operators");
    assert(
      parentKey.length === childKey.length,
      "The parentKey and childKey keys must have same length"
    );
    this.#parent = parent;
    this.#child = child;
    this.#storage = storage;
    this.#parentKey = parentKey;
    this.#childKey = childKey;
    this.#relationshipName = relationshipName;
    const parentSchema = parent.getSchema();
    const childSchema = child.getSchema();
    this.#schema = {
      ...parentSchema,
      relationships: {
        ...parentSchema.relationships,
        [relationshipName]: {
          ...childSchema,
          isHidden: hidden,
          system
        }
      }
    };
    parent.setOutput({
      push: (change) => this.#pushParent(change)
    });
    child.setOutput({
      push: (change) => this.#pushChild(change)
    });
  }
  destroy() {
    this.#parent.destroy();
    this.#child.destroy();
  }
  setOutput(output) {
    this.#output = output;
  }
  getSchema() {
    return this.#schema;
  }
  *fetch(req) {
    for (const parentNode of this.#parent.fetch(req)) {
      yield this.#processParentNode(
        parentNode.row,
        parentNode.relationships,
        "fetch"
      );
    }
  }
  *cleanup(req) {
    for (const parentNode of this.#parent.cleanup(req)) {
      yield this.#processParentNode(
        parentNode.row,
        parentNode.relationships,
        "cleanup"
      );
    }
  }
  #pushParent(change) {
    switch (change.type) {
      case "add":
        this.#output.push({
          type: "add",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          )
        });
        break;
      case "remove":
        this.#output.push({
          type: "remove",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "cleanup"
          )
        });
        break;
      case "child":
        this.#output.push({
          type: "child",
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          ),
          child: change.child
        });
        break;
      case "edit": {
        assert(
          rowEqualsForCompoundKey(
            change.oldNode.row,
            change.node.row,
            this.#parentKey
          ),
          `Parent edit must not change relationship.`
        );
        this.#output.push({
          type: "edit",
          oldNode: this.#processParentNode(
            change.oldNode.row,
            change.oldNode.relationships,
            "cleanup"
          ),
          node: this.#processParentNode(
            change.node.row,
            change.node.relationships,
            "fetch"
          )
        });
        break;
      }
      default:
        unreachable(change);
    }
  }
  #pushChild(change) {
    const pushChildChange = (childRow, change2) => {
      this.#inprogressChildChange = {
        change: change2,
        position: void 0
      };
      try {
        const parentNodes = this.#parent.fetch({
          constraint: Object.fromEntries(
            this.#parentKey.map((key, i) => [key, childRow[this.#childKey[i]]])
          )
        });
        for (const parentNode of parentNodes) {
          this.#inprogressChildChange.position = parentNode.row;
          const childChange = {
            type: "child",
            node: this.#processParentNode(
              parentNode.row,
              parentNode.relationships,
              "fetch"
            ),
            child: {
              relationshipName: this.#relationshipName,
              change: change2
            }
          };
          this.#output.push(childChange);
        }
      } finally {
        this.#inprogressChildChange = void 0;
      }
    };
    switch (change.type) {
      case "add":
      case "remove":
        pushChildChange(change.node.row, change);
        break;
      case "child":
        pushChildChange(change.node.row, change);
        break;
      case "edit": {
        const childRow = change.node.row;
        const oldChildRow = change.oldNode.row;
        assert(
          rowEqualsForCompoundKey(oldChildRow, childRow, this.#childKey),
          "Child edit must not change relationship."
        );
        pushChildChange(childRow, change);
        break;
      }
      default:
        unreachable(change);
    }
  }
  *#generateChildStreamWithOverlay(stream, overlay) {
    let applied = false;
    let editOldApplied = false;
    let editNewApplied = false;
    for (const child of stream) {
      let yieldChild = true;
      if (!applied) {
        switch (overlay.type) {
          case "add": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              applied = true;
              yieldChild = false;
            }
            break;
          }
          case "remove": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) < 0) {
              applied = true;
              yield overlay.node;
            }
            break;
          }
          case "edit": {
            if (this.#child.getSchema().compareRows(overlay.oldNode.row, child.row) < 0) {
              editOldApplied = true;
              if (editNewApplied) {
                applied = true;
              }
              yield overlay.oldNode;
            }
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              editNewApplied = true;
              if (editOldApplied) {
                applied = true;
              }
              yieldChild = false;
            }
            break;
          }
          case "child": {
            if (this.#child.getSchema().compareRows(overlay.node.row, child.row) === 0) {
              applied = true;
              yield {
                row: child.row,
                relationships: {
                  ...child.relationships,
                  [overlay.child.relationshipName]: () => this.#generateChildStreamWithOverlay(
                    child.relationships[overlay.child.relationshipName](),
                    overlay.child.change
                  )
                }
              };
              yieldChild = false;
            }
            break;
          }
        }
      }
      if (yieldChild) {
        yield child;
      }
    }
    if (!applied) {
      if (overlay.type === "remove") {
        applied = true;
        yield overlay.node;
      } else if (overlay.type === "edit") {
        assert(editNewApplied);
        editOldApplied = true;
        applied = true;
        yield overlay.oldNode;
      }
    }
    assert(applied);
  }
  #processParentNode(parentNodeRow, parentNodeRelations, mode) {
    let method = mode;
    let storageUpdated = false;
    const childStream = () => {
      if (!storageUpdated) {
        if (mode === "cleanup") {
          this.#storage.del(
            makeStorageKey(
              this.#parentKey,
              this.#parent.getSchema().primaryKey,
              parentNodeRow
            )
          );
          const empty = [
            ...take(
              this.#storage.scan({
                prefix: makeStorageKeyPrefix(parentNodeRow, this.#parentKey)
              }),
              1
            )
          ].length === 0;
          method = empty ? "cleanup" : "fetch";
        }
        storageUpdated = true;
        if (mode === "fetch") {
          this.#storage.set(
            makeStorageKey(
              this.#parentKey,
              this.#parent.getSchema().primaryKey,
              parentNodeRow
            ),
            true
          );
        }
      }
      const stream = this.#child[method]({
        constraint: Object.fromEntries(
          this.#childKey.map((key, i) => [
            key,
            parentNodeRow[this.#parentKey[i]]
          ])
        )
      });
      if (this.#inprogressChildChange && this.#isJoinMatch(
        parentNodeRow,
        this.#inprogressChildChange.change.node.row
      ) && this.#inprogressChildChange.position && this.#schema.compareRows(
        parentNodeRow,
        this.#inprogressChildChange.position
      ) > 0) {
        return this.#generateChildStreamWithOverlay(
          stream,
          this.#inprogressChildChange.change
        );
      }
      return stream;
    };
    return {
      row: parentNodeRow,
      relationships: {
        ...parentNodeRelations,
        [this.#relationshipName]: childStream
      }
    };
  }
  #isJoinMatch(parent, child) {
    for (let i = 0; i < this.#parentKey.length; i++) {
      if (!valuesEqual(parent[this.#parentKey[i]], child[this.#childKey[i]])) {
        return false;
      }
    }
    return true;
  }
};
function makeStorageKeyForValues(values) {
  const json = JSON.stringify(["pKeySet", ...values]);
  return json.substring(1, json.length - 1) + ",";
}
function makeStorageKeyPrefix(row, key) {
  return makeStorageKeyForValues(key.map((k) => row[k]));
}
function makeStorageKey(key, primaryKey, row) {
  const values = key.map((k) => row[k]);
  for (const key2 of primaryKey) {
    values.push(row[key2]);
  }
  return makeStorageKeyForValues(values);
}
function rowEqualsForCompoundKey(a, b, key) {
  for (let i = 0; i < key.length; i++) {
    if (compareValues(a[key[i]], b[key[i]]) !== 0) {
      return false;
    }
  }
  return true;
}

// ../zql/src/ivm/skip.ts
var Skip = class {
  #input;
  #bound;
  #comparator;
  #output = throwOutput;
  constructor(input, bound) {
    this.#input = input;
    this.#bound = bound;
    this.#comparator = input.getSchema().compareRows;
    input.setOutput(this);
  }
  getSchema() {
    return this.#input.getSchema();
  }
  fetch(req) {
    return this.#fetchOrCleanup("fetch", req);
  }
  cleanup(req) {
    return this.#fetchOrCleanup("fetch", req);
  }
  *#fetchOrCleanup(method, req) {
    const start = this.#getStart(req);
    if (start === "empty") {
      return;
    }
    const nodes = this.#input[method]({ ...req, start });
    if (!req.reverse) {
      yield* nodes;
      return;
    }
    for (const node of nodes) {
      if (!this.#shouldBePresent(node.row)) {
        return;
      }
      yield node;
    }
  }
  setOutput(output) {
    this.#output = output;
  }
  destroy() {
    this.#input.destroy();
  }
  #shouldBePresent(row) {
    const cmp2 = this.#comparator(this.#bound.row, row);
    return cmp2 < 0 || cmp2 === 0 && !this.#bound.exclusive;
  }
  push(change) {
    const shouldBePresent = (row) => this.#shouldBePresent(row);
    if (change.type === "edit") {
      maybeSplitAndPushEditChange(change, shouldBePresent, this.#output);
      return;
    }
    change;
    if (shouldBePresent(change.node.row)) {
      this.#output.push(change);
    }
  }
  #getStart(req) {
    const boundStart = {
      row: this.#bound.row,
      basis: this.#bound.exclusive ? "after" : "at"
    };
    if (!req.start) {
      if (req.reverse) {
        return void 0;
      }
      return boundStart;
    }
    const cmp2 = this.#comparator(this.#bound.row, req.start.row);
    if (!req.reverse) {
      if (cmp2 > 0) {
        return boundStart;
      }
      if (cmp2 === 0) {
        if (this.#bound.exclusive || req.start.basis === "after") {
          return {
            row: this.#bound.row,
            basis: "after"
          };
        }
        return boundStart;
      }
      return req.start;
    }
    req.reverse;
    if (cmp2 > 0) {
      return "empty";
    }
    if (cmp2 === 0) {
      if (!this.#bound.exclusive && req.start.basis === "at") {
        return boundStart;
      }
      return "empty";
    }
    return req.start;
  }
};

// ../zql/src/ivm/take.ts
var MAX_BOUND_KEY = "maxBound";
var Take = class {
  #input;
  #storage;
  #limit;
  #partitionKey;
  #partitionKeyComparator;
  // Fetch overlay needed for some split push cases.
  #rowHiddenFromFetch;
  #output = throwOutput;
  constructor(input, storage, limit, partitionKey) {
    assert(limit >= 0);
    assertOrderingIncludesPK(
      input.getSchema().sort,
      input.getSchema().primaryKey
    );
    input.setOutput(this);
    this.#input = input;
    this.#storage = storage;
    this.#limit = limit;
    this.#partitionKey = partitionKey;
    this.#partitionKeyComparator = partitionKey && makePartitionKeyComparator(partitionKey);
  }
  setOutput(output) {
    this.#output = output;
  }
  getSchema() {
    return this.#input.getSchema();
  }
  *fetch(req) {
    if (!this.#partitionKey || req.constraint && constraintMatchesPartitionKey(req.constraint, this.#partitionKey)) {
      const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
      const takeState = this.#storage.get(takeStateKey);
      if (!takeState) {
        yield* this.#initialFetch(req);
        return;
      }
      if (takeState.bound === void 0) {
        return;
      }
      for (const inputNode of this.#input.fetch(req)) {
        if (this.getSchema().compareRows(takeState.bound, inputNode.row) < 0) {
          return;
        }
        if (this.#rowHiddenFromFetch && this.getSchema().compareRows(
          this.#rowHiddenFromFetch,
          inputNode.row
        ) === 0) {
          continue;
        }
        yield inputNode;
      }
      return;
    }
    const maxBound = this.#storage.get(MAX_BOUND_KEY);
    if (maxBound === void 0) {
      return;
    }
    for (const inputNode of this.#input.fetch(req)) {
      if (this.getSchema().compareRows(inputNode.row, maxBound) > 0) {
        return;
      }
      const takeStateKey = getTakeStateKey(this.#partitionKey, inputNode.row);
      const takeState = this.#storage.get(takeStateKey);
      if (takeState?.bound !== void 0 && this.getSchema().compareRows(takeState.bound, inputNode.row) >= 0) {
        yield inputNode;
      }
    }
  }
  *#initialFetch(req) {
    assert(req.start === void 0);
    assert(!req.reverse);
    assert(constraintMatchesPartitionKey(req.constraint, this.#partitionKey));
    if (this.#limit === 0) {
      return;
    }
    const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
    assert(this.#storage.get(takeStateKey) === void 0);
    let size = 0;
    let bound;
    let downstreamEarlyReturn = true;
    let exceptionThrown = false;
    try {
      for (const inputNode of this.#input.fetch(req)) {
        yield inputNode;
        bound = inputNode.row;
        size++;
        if (size === this.#limit) {
          break;
        }
      }
      downstreamEarlyReturn = false;
    } catch (e) {
      exceptionThrown = true;
      throw e;
    } finally {
      if (!exceptionThrown) {
        this.#setTakeState(
          takeStateKey,
          size,
          bound,
          this.#storage.get(MAX_BOUND_KEY)
        );
        assert(
          !downstreamEarlyReturn,
          "Unexpected early return prevented full hydration"
        );
      }
    }
  }
  *cleanup(req) {
    assert(req.start === void 0);
    assert(constraintMatchesPartitionKey(req.constraint, this.#partitionKey));
    const takeStateKey = getTakeStateKey(this.#partitionKey, req.constraint);
    this.#storage.del(takeStateKey);
    let size = 0;
    for (const inputNode of this.#input.cleanup(req)) {
      if (size === this.#limit) {
        return;
      }
      size++;
      yield inputNode;
    }
  }
  #getStateAndConstraint(row) {
    const takeStateKey = getTakeStateKey(this.#partitionKey, row);
    const takeState = this.#storage.get(takeStateKey);
    let maxBound;
    let constraint;
    if (takeState) {
      maxBound = this.#storage.get(MAX_BOUND_KEY);
      constraint = this.#partitionKey && Object.fromEntries(
        this.#partitionKey.map((key) => [key, row[key]])
      );
    }
    return { takeState, takeStateKey, maxBound, constraint };
  }
  push(change) {
    if (change.type === "edit") {
      this.#pushEditChange(change);
      return;
    }
    const { takeState, takeStateKey, maxBound, constraint } = this.#getStateAndConstraint(change.node.row);
    if (!takeState) {
      return;
    }
    const { compareRows } = this.getSchema();
    if (change.type === "add") {
      if (takeState.size < this.#limit) {
        this.#setTakeState(
          takeStateKey,
          takeState.size + 1,
          takeState.bound === void 0 || compareRows(takeState.bound, change.node.row) < 0 ? change.node.row : takeState.bound,
          maxBound
        );
        this.#output.push(change);
        return;
      }
      if (takeState.bound === void 0 || compareRows(change.node.row, takeState.bound) >= 0) {
        return;
      }
      let beforeBoundNode;
      let boundNode;
      if (this.#limit === 1) {
        boundNode = must(
          first(
            this.#input.fetch({
              start: {
                row: takeState.bound,
                basis: "at"
              },
              constraint
            })
          )
        );
      } else {
        [boundNode, beforeBoundNode] = take(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "at"
            },
            constraint,
            reverse: true
          }),
          2
        );
      }
      const removeChange = {
        type: "remove",
        node: boundNode
      };
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        beforeBoundNode === void 0 || compareRows(change.node.row, beforeBoundNode.row) > 0 ? change.node.row : beforeBoundNode.row,
        maxBound
      );
      this.#withRowHiddenFromFetch(change.node.row, () => {
        this.#output.push(removeChange);
      });
      this.#output.push(change);
    } else if (change.type === "remove") {
      if (takeState.bound === void 0) {
        return;
      }
      const compToBound = compareRows(change.node.row, takeState.bound);
      if (compToBound > 0) {
        return;
      }
      const [beforeBoundNode] = take(
        this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "after"
          },
          constraint,
          reverse: true
        }),
        1
      );
      let newBound;
      if (beforeBoundNode) {
        const push = compareRows(beforeBoundNode.row, takeState.bound) > 0;
        newBound = {
          node: beforeBoundNode,
          push
        };
      }
      if (!newBound?.push) {
        for (const node of this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "at"
          },
          constraint
        })) {
          const push = compareRows(node.row, takeState.bound) > 0;
          newBound = {
            node,
            push
          };
          if (push) {
            break;
          }
        }
      }
      if (newBound?.push) {
        this.#output.push(change);
        this.#setTakeState(
          takeStateKey,
          takeState.size,
          newBound.node.row,
          maxBound
        );
        this.#output.push({
          type: "add",
          node: newBound.node
        });
        return;
      }
      this.#setTakeState(
        takeStateKey,
        takeState.size - 1,
        newBound?.node.row,
        maxBound
      );
      this.#output.push(change);
    } else if (change.type === "child") {
      if (takeState.bound && compareRows(change.node.row, takeState.bound) <= 0) {
        this.#output.push(change);
      }
    }
  }
  #pushEditChange(change) {
    assert(
      !this.#partitionKeyComparator || this.#partitionKeyComparator(change.oldNode.row, change.node.row) === 0,
      "Unexpected change of partition key"
    );
    const { takeState, takeStateKey, maxBound, constraint } = this.#getStateAndConstraint(change.oldNode.row);
    if (!takeState) {
      return;
    }
    assert(takeState.bound, "Bound should be set");
    const { compareRows } = this.getSchema();
    const oldCmp = compareRows(change.oldNode.row, takeState.bound);
    const newCmp = compareRows(change.node.row, takeState.bound);
    const replaceBoundAndForwardChange = () => {
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        change.node.row,
        maxBound
      );
      this.#output.push(change);
    };
    if (oldCmp === 0) {
      if (newCmp === 0) {
        this.#output.push(change);
        return;
      }
      if (newCmp < 0) {
        if (this.#limit === 1) {
          replaceBoundAndForwardChange();
          return;
        }
        const beforeBoundNode = must(
          first(
            this.#input.fetch({
              start: {
                row: takeState.bound,
                basis: "after"
              },
              constraint,
              reverse: true
            })
          )
        );
        this.#setTakeState(
          takeStateKey,
          takeState.size,
          beforeBoundNode.row,
          maxBound
        );
        this.#output.push(change);
        return;
      }
      assert(newCmp > 0);
      const newBoundNode = must(
        first(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "at"
            },
            constraint
          })
        )
      );
      if (compareRows(newBoundNode.row, change.node.row) === 0) {
        replaceBoundAndForwardChange();
        return;
      }
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        newBoundNode.row,
        maxBound
      );
      this.#output.push({
        type: "add",
        node: newBoundNode
      });
      return;
    }
    if (oldCmp > 0) {
      assert(newCmp !== 0, "Invalid state. Row has duplicate primary key");
      if (newCmp > 0) {
        return;
      }
      assert(newCmp < 0);
      const [oldBoundNode, newBoundNode] = take(
        this.#input.fetch({
          start: {
            row: takeState.bound,
            basis: "at"
          },
          constraint,
          reverse: true
        }),
        2
      );
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        newBoundNode.row,
        maxBound
      );
      this.#withRowHiddenFromFetch(change.node.row, () => {
        this.#output.push({
          type: "remove",
          node: oldBoundNode
        });
      });
      this.#output.push({
        type: "add",
        node: change.node
      });
      return;
    }
    if (oldCmp < 0) {
      assert(newCmp !== 0, "Invalid state. Row has duplicate primary key");
      if (newCmp < 0) {
        this.#output.push(change);
        return;
      }
      assert(newCmp > 0);
      const afterBoundNode = must(
        first(
          this.#input.fetch({
            start: {
              row: takeState.bound,
              basis: "after"
            },
            constraint
          })
        )
      );
      if (compareRows(afterBoundNode.row, change.node.row) === 0) {
        replaceBoundAndForwardChange();
        return;
      }
      this.#output.push({
        type: "remove",
        node: change.oldNode
      });
      this.#setTakeState(
        takeStateKey,
        takeState.size,
        afterBoundNode.row,
        maxBound
      );
      this.#output.push({
        type: "add",
        node: afterBoundNode
      });
      return;
    }
    unreachable();
  }
  #withRowHiddenFromFetch(row, fn) {
    this.#rowHiddenFromFetch = row;
    try {
      fn();
    } finally {
      this.#rowHiddenFromFetch = void 0;
    }
  }
  #setTakeState(takeStateKey, size, bound, maxBound) {
    this.#storage.set(takeStateKey, {
      size,
      bound
    });
    if (bound !== void 0 && (maxBound === void 0 || this.getSchema().compareRows(bound, maxBound) > 0)) {
      this.#storage.set(MAX_BOUND_KEY, bound);
    }
  }
  destroy() {
    this.#input.destroy();
  }
};
function getTakeStateKey(partitionKey, rowOrConstraint) {
  const partitionValues = [];
  if (partitionKey && rowOrConstraint) {
    for (const key of partitionKey) {
      partitionValues.push(rowOrConstraint[key]);
    }
  }
  return JSON.stringify(["take", ...partitionValues]);
}
function constraintMatchesPartitionKey(constraint, partitionKey) {
  if (constraint === void 0 || partitionKey === void 0) {
    return constraint === partitionKey;
  }
  if (partitionKey.length !== Object.keys(constraint).length) {
    return false;
  }
  for (const key of partitionKey) {
    if (!hasOwn(constraint, key)) {
      return false;
    }
  }
  return true;
}
function makePartitionKeyComparator(partitionKey) {
  return (a, b) => {
    for (const key of partitionKey) {
      const cmp2 = compareValues(a[key], b[key]);
      if (cmp2 !== 0) {
        return cmp2;
      }
    }
    return 0;
  };
}

// ../zql/src/query/expression.ts
var ExpressionBuilder = class {
  #exists;
  constructor(exists) {
    this.#exists = exists;
    this.exists = this.exists.bind(this);
  }
  get eb() {
    return this;
  }
  cmp(field, opOrValue, value) {
    return cmp(field, opOrValue, value);
  }
  cmpLit(left, op, right) {
    return {
      type: "simple",
      left: isParameterReference(left) ? left[toStaticParam]() : { type: "literal", value: left },
      right: isParameterReference(right) ? right[toStaticParam]() : { type: "literal", value: right },
      op
    };
  }
  and = and;
  or = or;
  not = not;
  exists = (relationship, cb) => this.#exists(relationship, cb);
};
function and(...conditions) {
  const expressions = filterTrue(filterUndefined(conditions));
  if (expressions.length === 1) {
    return expressions[0];
  }
  if (expressions.some(isAlwaysFalse)) {
    return FALSE;
  }
  return { type: "and", conditions: expressions };
}
function or(...conditions) {
  const expressions = filterFalse(filterUndefined(conditions));
  if (expressions.length === 1) {
    return expressions[0];
  }
  if (expressions.some(isAlwaysTrue)) {
    return TRUE;
  }
  return { type: "or", conditions: expressions };
}
function not(expression) {
  switch (expression.type) {
    case "and":
      return {
        type: "or",
        conditions: expression.conditions.map(not)
      };
    case "or":
      return {
        type: "and",
        conditions: expression.conditions.map(not)
      };
    case "correlatedSubquery":
      return {
        type: "correlatedSubquery",
        related: expression.related,
        op: negateOperator(expression.op)
      };
    case "simple":
      return {
        type: "simple",
        op: negateOperator(expression.op),
        left: expression.left,
        right: expression.right
      };
  }
}
function cmp(field, opOrValue, value) {
  let op;
  if (value === void 0) {
    value = opOrValue;
    op = "=";
  } else {
    op = opOrValue;
  }
  return {
    type: "simple",
    left: { type: "column", name: field },
    right: isParameterReference(value) ? value[toStaticParam]() : { type: "literal", value },
    op
  };
}
function isParameterReference(value) {
  return value !== null && typeof value === "object" && value[toStaticParam];
}
var TRUE = {
  type: "and",
  conditions: []
};
var FALSE = {
  type: "or",
  conditions: []
};
function isAlwaysTrue(condition) {
  return condition.type === "and" && condition.conditions.length === 0;
}
function isAlwaysFalse(condition) {
  return condition.type === "or" && condition.conditions.length === 0;
}
function simplifyCondition(c) {
  if (c.type === "simple" || c.type === "correlatedSubquery") {
    return c;
  }
  if (c.conditions.length === 1) {
    return simplifyCondition(c.conditions[0]);
  }
  const conditions = flatten(c.type, c.conditions.map(simplifyCondition));
  if (c.type === "and" && conditions.some(isAlwaysFalse)) {
    return FALSE;
  }
  if (c.type === "or" && conditions.some(isAlwaysTrue)) {
    return TRUE;
  }
  return {
    type: c.type,
    conditions
  };
}
function flatten(type, conditions) {
  const flattened2 = [];
  for (const c of conditions) {
    if (c.type === type) {
      flattened2.push(...c.conditions);
    } else {
      flattened2.push(c);
    }
  }
  return flattened2;
}
var negateSimpleOperatorMap = {
  ["="]: "!=",
  ["!="]: "=",
  ["<"]: ">=",
  [">"]: "<=",
  [">="]: "<",
  ["<="]: ">",
  ["IN"]: "NOT IN",
  ["NOT IN"]: "IN",
  ["LIKE"]: "NOT LIKE",
  ["NOT LIKE"]: "LIKE",
  ["ILIKE"]: "NOT ILIKE",
  ["NOT ILIKE"]: "ILIKE",
  ["IS"]: "IS NOT",
  ["IS NOT"]: "IS"
};
var negateOperatorMap = {
  ...negateSimpleOperatorMap,
  ["EXISTS"]: "NOT EXISTS",
  ["NOT EXISTS"]: "EXISTS"
};
function negateOperator(op) {
  return must(negateOperatorMap[op]);
}
function filterUndefined(array3) {
  return array3.filter((e) => e !== void 0);
}
function filterTrue(conditions) {
  return conditions.filter((c) => !isAlwaysTrue(c));
}
function filterFalse(conditions) {
  return conditions.filter((c) => !isAlwaysFalse(c));
}

// ../zql/src/builder/like.ts
function getLikePredicate(pattern, flags) {
  const op = getLikeOp(String(pattern), flags);
  return (lhs) => {
    assertString(lhs);
    return op(String(lhs));
  };
}
function getLikeOp(pattern, flags) {
  if (!/_|%|\\/.test(pattern)) {
    if (flags === "i") {
      const rhsLower = pattern.toLowerCase();
      return (lhs) => lhs.toLowerCase() === rhsLower;
    }
    return (lhs) => lhs === pattern;
  }
  const re = patternToRegExp(pattern, flags);
  return (lhs) => re.test(lhs);
}
var specialCharsRe = /[$()*+.?[\]\\^{|}]/;
function patternToRegExp(source, flags = "") {
  let pattern = "^";
  for (let i = 0; i < source.length; i++) {
    let c = source[i];
    switch (c) {
      case "%":
        pattern += ".*";
        break;
      case "_":
        pattern += ".";
        break;
      // @ts-expect-error fallthrough
      case "\\":
        if (i === source.length - 1) {
          throw new Error("LIKE pattern must not end with escape character");
        }
        i++;
        c = source[i];
      // fall through
      default:
        if (specialCharsRe.test(c)) {
          pattern += "\\";
        }
        pattern += c;
        break;
    }
  }
  return new RegExp(pattern + "$", flags + "m");
}

// ../zql/src/builder/filter.ts
function createPredicate(condition) {
  if (condition.type !== "simple") {
    const predicates = condition.conditions.map((c) => createPredicate(c));
    return condition.type === "and" ? (row) => {
      for (const predicate of predicates) {
        if (!predicate(row)) {
          return false;
        }
      }
      return true;
    } : (row) => {
      for (const predicate of predicates) {
        if (predicate(row)) {
          return true;
        }
      }
      return false;
    };
  }
  const { left } = condition;
  const { right } = condition;
  assert(
    right.type !== "static",
    "static values should be resolved before creating predicates"
  );
  assert(
    left.type !== "static",
    "static values should be resolved before creating predicates"
  );
  switch (condition.op) {
    case "IS":
    case "IS NOT": {
      const impl2 = createIsPredicate(right.value, condition.op);
      if (left.type === "literal") {
        const result = impl2(left.value);
        return () => result;
      }
      return (row) => impl2(row[left.name]);
    }
  }
  if (right.value === null || right.value === void 0) {
    return (_row) => false;
  }
  const impl = createPredicateImpl(right.value, condition.op);
  if (left.type === "literal") {
    if (left.value === null || left.value === void 0) {
      return (_row) => false;
    }
    const result = impl(left.value);
    return () => result;
  }
  return (row) => {
    const lhs = row[left.name];
    if (lhs === null || lhs === void 0) {
      return false;
    }
    return impl(lhs);
  };
}
function createIsPredicate(rhs, operator) {
  switch (operator) {
    case "IS":
      return (lhs) => lhs === rhs;
    case "IS NOT":
      return (lhs) => lhs !== rhs;
  }
}
function createPredicateImpl(rhs, operator) {
  switch (operator) {
    case "=":
      return (lhs) => lhs === rhs;
    case "!=":
      return (lhs) => lhs !== rhs;
    case "<":
      return (lhs) => lhs < rhs;
    case "<=":
      return (lhs) => lhs <= rhs;
    case ">":
      return (lhs) => lhs > rhs;
    case ">=":
      return (lhs) => lhs >= rhs;
    case "LIKE":
      return getLikePredicate(rhs, "");
    case "NOT LIKE":
      return not2(getLikePredicate(rhs, ""));
    case "ILIKE":
      return getLikePredicate(rhs, "i");
    case "NOT ILIKE":
      return not2(getLikePredicate(rhs, "i"));
    case "IN": {
      assert(Array.isArray(rhs));
      const set = new Set(rhs);
      return (lhs) => set.has(lhs);
    }
    case "NOT IN": {
      assert(Array.isArray(rhs));
      const set = new Set(rhs);
      return (lhs) => !set.has(lhs);
    }
    default:
      operator;
      throw new Error(`Unexpected operator: ${operator}`);
  }
}
function not2(f) {
  return (lhs) => !f(lhs);
}
function transformFilters(filters) {
  if (!filters) {
    return { filters: void 0, conditionsRemoved: false };
  }
  switch (filters.type) {
    case "simple":
      return { filters, conditionsRemoved: false };
    case "correlatedSubquery":
      return { filters: void 0, conditionsRemoved: true };
    case "and":
    case "or": {
      const transformedConditions = [];
      let conditionsRemoved = false;
      for (const cond of filters.conditions) {
        const transformed = transformFilters(cond);
        if (transformed.filters === void 0 && filters.type === "or") {
          return { filters: void 0, conditionsRemoved: true };
        }
        conditionsRemoved = conditionsRemoved || transformed.conditionsRemoved;
        if (transformed.filters) {
          transformedConditions.push(transformed.filters);
        }
      }
      return {
        filters: simplifyCondition({
          type: filters.type,
          conditions: transformedConditions
        }),
        conditionsRemoved
      };
    }
    default:
      unreachable(filters);
  }
}

// ../zql/src/builder/builder.ts
function buildPipeline(ast, delegate) {
  return buildPipelineInternal(
    delegate.mapAst ? delegate.mapAst(ast) : ast,
    delegate,
    ""
  );
}
function buildPipelineInternal(ast, delegate, name, partitionKey) {
  const source = delegate.getSource(ast.table);
  if (!source) {
    throw new Error(`Source not found: ${ast.table}`);
  }
  ast = uniquifyCorrelatedSubqueryConditionAliases(ast);
  const csqsFromCondition = gatherCorrelatedSubqueryQueriesFromCondition(
    ast.where
  );
  const splitEditKeys = partitionKey ? new Set(partitionKey) : /* @__PURE__ */ new Set();
  const aliases = /* @__PURE__ */ new Set();
  for (const csq of csqsFromCondition) {
    aliases.add(csq.subquery.alias || "");
    for (const key of csq.correlation.parentField) {
      splitEditKeys.add(key);
    }
  }
  if (ast.related) {
    for (const csq of ast.related) {
      for (const key of csq.correlation.parentField) {
        splitEditKeys.add(key);
      }
    }
  }
  const conn = source.connect(must(ast.orderBy), ast.where, splitEditKeys);
  let end = delegate.decorateInput(conn, `${name}:source(${ast.table})`);
  const { fullyAppliedFilters } = conn;
  if (ast.start) {
    end = delegate.decorateInput(new Skip(end, ast.start), `${name}:skip)`);
  }
  for (const csq of csqsFromCondition) {
    end = applyCorrelatedSubQuery(csq, delegate, end, name);
  }
  if (ast.where && !fullyAppliedFilters) {
    end = applyWhere(end, ast.where, delegate, name);
  }
  if (ast.limit !== void 0) {
    const takeName = `${name}:take`;
    end = delegate.decorateInput(
      new Take(end, delegate.createStorage(takeName), ast.limit, partitionKey),
      takeName
    );
  }
  if (ast.related) {
    for (const csq of ast.related) {
      end = applyCorrelatedSubQuery(csq, delegate, end, name);
    }
  }
  return end;
}
function applyWhere(input, condition, delegate, name) {
  return buildFilterPipeline(
    input,
    (filterInput) => applyFilter(filterInput, condition, delegate, name)
  );
}
function applyFilter(input, condition, delegate, name) {
  switch (condition.type) {
    case "and":
      return applyAnd(input, condition, delegate, name);
    case "or":
      return applyOr(input, condition, delegate, name);
    case "correlatedSubquery":
      return applyCorrelatedSubqueryCondition(input, condition, delegate, name);
    case "simple":
      return applySimpleCondition(input, condition);
  }
}
function applyAnd(input, condition, delegate, name) {
  for (const subCondition of condition.conditions) {
    input = applyFilter(input, subCondition, delegate, name);
  }
  return input;
}
function applyOr(input, condition, delegate, name) {
  const [subqueryConditions, otherConditions] = groupSubqueryConditions(condition);
  if (subqueryConditions.length === 0) {
    return new Filter(
      input,
      createPredicate({
        type: "or",
        conditions: otherConditions
      })
    );
  }
  const fanOut = new FanOut(input);
  const branches = subqueryConditions.map(
    (subCondition) => applyFilter(fanOut, subCondition, delegate, name)
  );
  if (otherConditions.length > 0) {
    branches.push(
      new Filter(
        fanOut,
        createPredicate({
          type: "or",
          conditions: otherConditions
        })
      )
    );
  }
  const ret = new FanIn(fanOut, branches);
  fanOut.setFanIn(ret);
  return ret;
}
function groupSubqueryConditions(condition) {
  const partitioned = [[], []];
  for (const subCondition of condition.conditions) {
    if (isNotAndDoesNotContainSubquery(subCondition)) {
      partitioned[1].push(subCondition);
    } else {
      partitioned[0].push(subCondition);
    }
  }
  return partitioned;
}
function isNotAndDoesNotContainSubquery(condition) {
  if (condition.type === "correlatedSubquery") {
    return false;
  }
  if (condition.type === "simple") {
    return true;
  }
  return condition.conditions.every(isNotAndDoesNotContainSubquery);
}
function applySimpleCondition(input, condition) {
  return new Filter(input, createPredicate(condition));
}
function applyCorrelatedSubQuery(sq, delegate, end, name) {
  assert(sq.subquery.alias, "Subquery must have an alias");
  const child = buildPipelineInternal(
    sq.subquery,
    delegate,
    `${name}.${sq.subquery.alias}`,
    sq.correlation.childField
  );
  const joinName = `${name}:join(${sq.subquery.alias})`;
  end = new Join({
    parent: end,
    child,
    storage: delegate.createStorage(joinName),
    parentKey: sq.correlation.parentField,
    childKey: sq.correlation.childField,
    relationshipName: sq.subquery.alias,
    hidden: sq.hidden ?? false,
    system: sq.system ?? "client"
  });
  return delegate.decorateInput(end, joinName);
}
function applyCorrelatedSubqueryCondition(input, condition, delegate, name) {
  assert(condition.op === "EXISTS" || condition.op === "NOT EXISTS");
  const existsName = `${name}:exists(${condition.related.subquery.alias})`;
  return delegate.decorateFilterInput(
    new Exists(
      input,
      delegate.createStorage(existsName),
      must(condition.related.subquery.alias),
      condition.related.correlation.parentField,
      condition.op
    ),
    existsName
  );
}
function gatherCorrelatedSubqueryQueriesFromCondition(condition) {
  const csqs = [];
  const gather = (condition2) => {
    if (condition2.type === "correlatedSubquery") {
      assert(condition2.op === "EXISTS" || condition2.op === "NOT EXISTS");
      csqs.push({
        ...condition2.related,
        subquery: {
          ...condition2.related.subquery,
          limit: condition2.related.system === "permissions" ? PERMISSIONS_EXISTS_LIMIT : EXISTS_LIMIT
        }
      });
      return;
    }
    if (condition2.type === "and" || condition2.type === "or") {
      for (const c of condition2.conditions) {
        gather(c);
      }
      return;
    }
  };
  if (condition) {
    gather(condition);
  }
  return csqs;
}
var EXISTS_LIMIT = 3;
var PERMISSIONS_EXISTS_LIMIT = 1;
function assertOrderingIncludesPK(ordering, pk) {
  const orderingFields = ordering.map(([field]) => field);
  const missingFields = pk.filter((pkField) => !orderingFields.includes(pkField));
  if (missingFields.length > 0) {
    throw new Error(
      `Ordering must include all primary key fields. Missing: ${missingFields.join(
        ", "
      )}. ZQL automatically appends primary key fields to the ordering if they are missing 
      so a common cause of this error is a casing mismatch between Postgres and ZQL.
      E.g., "userid" vs "userID".
      You may want to add double-quotes around your Postgres column names to prevent Postgres from lower-casing them:
      https://www.postgresql.org/docs/current/sql-syntax-lexical.htm`
    );
  }
}
function uniquifyCorrelatedSubqueryConditionAliases(ast) {
  if (!ast.where) {
    return ast;
  }
  const { where } = ast;
  if (where.type !== "and" && where.type !== "or") {
    return ast;
  }
  let count = 0;
  const uniquifyCorrelatedSubquery = (csqc) => ({
    ...csqc,
    related: {
      ...csqc.related,
      subquery: {
        ...csqc.related.subquery,
        alias: (csqc.related.subquery.alias ?? "") + "_" + count++
      }
    }
  });
  const uniquify = (cond) => {
    if (cond.type === "simple") {
      return cond;
    } else if (cond.type === "correlatedSubquery") {
      return uniquifyCorrelatedSubquery(cond);
    }
    const conditions = [];
    for (const c of cond.conditions) {
      conditions.push(uniquify(c));
    }
    return {
      type: cond.type,
      conditions
    };
  };
  const result = {
    ...ast,
    where: uniquify(where)
  };
  return result;
}

// ../zql/src/ivm/array-view.ts
var ArrayView = class {
  #input;
  #listeners = /* @__PURE__ */ new Set();
  #schema;
  #format;
  // Synthetic "root" entry that has a single "" relationship, so that we can
  // treat all changes, including the root change, generically.
  #root;
  onDestroy;
  #dirty = false;
  #complete = false;
  #updateTTL;
  constructor(input, format, queryComplete, updateTTL) {
    this.#input = input;
    this.#schema = input.getSchema();
    this.#format = format;
    this.#updateTTL = updateTTL;
    this.#root = { "": format.singular ? void 0 : [] };
    input.setOutput(this);
    if (queryComplete === true) {
      this.#complete = true;
    } else {
      void queryComplete.then(() => {
        this.#complete = true;
        this.#fireListeners();
      });
    }
    this.#hydrate();
  }
  get data() {
    return this.#root[""];
  }
  addListener(listener) {
    assert(!this.#listeners.has(listener), "Listener already registered");
    this.#listeners.add(listener);
    this.#fireListener(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  }
  #fireListeners() {
    for (const listener of this.#listeners) {
      this.#fireListener(listener);
    }
  }
  #fireListener(listener) {
    listener(
      this.data,
      this.#complete ? "complete" : "unknown"
    );
  }
  destroy() {
    this.onDestroy?.();
  }
  #hydrate() {
    this.#dirty = true;
    for (const node of this.#input.fetch({})) {
      applyChange(
        this.#root,
        { type: "add", node },
        this.#schema,
        "",
        this.#format
      );
    }
    this.flush();
  }
  push(change) {
    this.#dirty = true;
    applyChange(this.#root, change, this.#schema, "", this.#format);
  }
  flush() {
    if (!this.#dirty) {
      return;
    }
    this.#dirty = false;
    this.#fireListeners();
  }
  updateTTL(ttl) {
    this.#updateTTL(ttl);
  }
};

// ../zql/src/query/assert-no-not-exists.ts
function assertNoNotExists(condition) {
  switch (condition.type) {
    case "simple":
      return;
    case "correlatedSubquery":
      if (condition.op === "NOT EXISTS") {
        throw new Error(
          "not(exists()) is not supported on the client - see https://bugs.rocicorp.dev/issue/3438"
        );
      }
      if (condition.related.subquery.where) {
        assertNoNotExists(condition.related.subquery.where);
      }
      return;
    case "and":
    case "or":
      for (const c of condition.conditions) {
        assertNoNotExists(c);
      }
      return;
    default:
      unreachable(condition);
  }
}

// ../zql/src/query/query-impl.ts
var astSymbol = Symbol();
function newQuery(delegate, schema, table) {
  return new QueryImpl(delegate, schema, table, { table }, defaultFormat);
}
function staticParam(anchorClass, field) {
  return {
    type: "static",
    anchor: anchorClass,
    // for backwards compatibility
    field: field.length === 1 ? field[0] : field
  };
}
var SUBQ_PREFIX = "zsubq_";
var defaultFormat = { singular: false, relationships: {} };
var newQuerySymbol = Symbol();
var AbstractQuery = class {
  #schema;
  #tableName;
  #ast;
  format;
  #hash = "";
  #system;
  constructor(schema, tableName, ast, format, system) {
    this.#schema = schema;
    this.#tableName = tableName;
    this.#ast = ast;
    this.format = format;
    this.#system = system;
  }
  get [astSymbol]() {
    return this.#ast;
  }
  hash() {
    if (!this.#hash) {
      this.#hash = hashOfAST(this._completeAst());
    }
    return this.#hash;
  }
  one = () => this[newQuerySymbol](
    this.#schema,
    this.#tableName,
    {
      ...this.#ast,
      limit: 1
    },
    {
      ...this.format,
      singular: true
    }
  );
  whereExists = (relationship, cb) => this.where(({ exists }) => exists(relationship, cb));
  related = (relationship, cb) => {
    if (relationship.startsWith(SUBQ_PREFIX)) {
      throw new Error(
        `Relationship names may not start with "${SUBQ_PREFIX}". That is a reserved prefix.`
      );
    }
    cb = cb ?? ((q) => q);
    const related = this.#schema.relationships[this.#tableName][relationship];
    assert(related, "Invalid relationship");
    if (isOneHop(related)) {
      const { destSchema, destField, sourceField, cardinality } = related[0];
      let q = this[newQuerySymbol](
        this.#schema,
        destSchema,
        {
          table: destSchema,
          alias: relationship
        },
        {
          relationships: {},
          singular: cardinality === "one"
        }
      );
      if (cardinality === "one") {
        q = q.one();
      }
      const sq = cb(q);
      assert(
        isCompoundKey(sourceField),
        "The source of a relationship must specify at last 1 field"
      );
      assert(
        isCompoundKey(destField),
        "The destination of a relationship must specify at last 1 field"
      );
      assert(
        sourceField.length === destField.length,
        "The source and destination of a relationship must have the same number of fields"
      );
      return this[newQuerySymbol](
        this.#schema,
        this.#tableName,
        {
          ...this.#ast,
          related: [
            ...this.#ast.related ?? [],
            {
              system: this.#system,
              correlation: {
                parentField: sourceField,
                childField: destField
              },
              subquery: addPrimaryKeysToAst(
                this.#schema.tables[destSchema],
                sq.#ast
              )
            }
          ]
        },
        {
          ...this.format,
          relationships: {
            ...this.format.relationships,
            [relationship]: sq.format
          }
        }
      );
    }
    if (isTwoHop(related)) {
      const [firstRelation, secondRelation] = related;
      const { destSchema } = secondRelation;
      const junctionSchema = firstRelation.destSchema;
      const sq = cb(
        this[newQuerySymbol](
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: relationship
          },
          {
            relationships: {},
            singular: secondRelation.cardinality === "one"
          }
        )
      );
      assert(isCompoundKey(firstRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(firstRelation.destField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.destField), "Invalid relationship");
      return this[newQuerySymbol](
        this.#schema,
        this.#tableName,
        {
          ...this.#ast,
          related: [
            ...this.#ast.related ?? [],
            {
              system: this.#system,
              correlation: {
                parentField: firstRelation.sourceField,
                childField: firstRelation.destField
              },
              hidden: true,
              subquery: {
                table: junctionSchema,
                alias: relationship,
                orderBy: addPrimaryKeys(
                  this.#schema.tables[junctionSchema],
                  void 0
                ),
                related: [
                  {
                    system: this.#system,
                    correlation: {
                      parentField: secondRelation.sourceField,
                      childField: secondRelation.destField
                    },
                    subquery: addPrimaryKeysToAst(
                      this.#schema.tables[destSchema],
                      sq.#ast
                    )
                  }
                ]
              }
            }
          ]
        },
        {
          ...this.format,
          relationships: {
            ...this.format.relationships,
            [relationship]: sq.format
          }
        }
      );
    }
    throw new Error(`Invalid relationship ${relationship}`);
  };
  where = (fieldOrExpressionFactory, opOrValue, value) => {
    let cond;
    if (typeof fieldOrExpressionFactory === "function") {
      cond = fieldOrExpressionFactory(
        new ExpressionBuilder(this._exists)
      );
    } else {
      assert(opOrValue !== void 0, "Invalid condition");
      cond = cmp(fieldOrExpressionFactory, opOrValue, value);
    }
    const existingWhere = this.#ast.where;
    if (existingWhere) {
      cond = and(existingWhere, cond);
    }
    const where = simplifyCondition(cond);
    if (this.#system === "client") {
      assertNoNotExists(where);
    }
    return this[newQuerySymbol](
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        where
      },
      this.format
    );
  };
  start = (row, opts) => this[newQuerySymbol](
    this.#schema,
    this.#tableName,
    {
      ...this.#ast,
      start: {
        row,
        exclusive: !opts?.inclusive
      }
    },
    this.format
  );
  limit = (limit) => {
    if (limit < 0) {
      throw new Error("Limit must be non-negative");
    }
    if ((limit | 0) !== limit) {
      throw new Error("Limit must be an integer");
    }
    return this[newQuerySymbol](
      this.#schema,
      this.#tableName,
      {
        ...this.#ast,
        limit
      },
      this.format
    );
  };
  orderBy = (field, direction) => this[newQuerySymbol](
    this.#schema,
    this.#tableName,
    {
      ...this.#ast,
      orderBy: [...this.#ast.orderBy ?? [], [field, direction]]
    },
    this.format
  );
  _exists = (relationship, cb = (q) => q) => {
    const related = this.#schema.relationships[this.#tableName][relationship];
    assert(related, "Invalid relationship");
    if (isOneHop(related)) {
      const { destSchema, sourceField, destField } = related[0];
      assert(isCompoundKey(sourceField), "Invalid relationship");
      assert(isCompoundKey(destField), "Invalid relationship");
      const sq = cb(
        this[newQuerySymbol](
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: `${SUBQ_PREFIX}${relationship}`
          },
          defaultFormat
        )
      );
      return {
        type: "correlatedSubquery",
        related: {
          system: this.#system,
          correlation: {
            parentField: sourceField,
            childField: destField
          },
          subquery: addPrimaryKeysToAst(
            this.#schema.tables[destSchema],
            sq.#ast
          )
        },
        op: "EXISTS"
      };
    }
    if (isTwoHop(related)) {
      const [firstRelation, secondRelation] = related;
      assert(isCompoundKey(firstRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(firstRelation.destField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.sourceField), "Invalid relationship");
      assert(isCompoundKey(secondRelation.destField), "Invalid relationship");
      const { destSchema } = secondRelation;
      const junctionSchema = firstRelation.destSchema;
      const queryToDest = cb(
        this[newQuerySymbol](
          this.#schema,
          destSchema,
          {
            table: destSchema,
            alias: `${SUBQ_PREFIX}${relationship}`
          },
          defaultFormat
        )
      );
      return {
        type: "correlatedSubquery",
        related: {
          system: this.#system,
          correlation: {
            parentField: firstRelation.sourceField,
            childField: firstRelation.destField
          },
          subquery: {
            table: junctionSchema,
            alias: `${SUBQ_PREFIX}${relationship}`,
            orderBy: addPrimaryKeys(
              this.#schema.tables[junctionSchema],
              void 0
            ),
            where: {
              type: "correlatedSubquery",
              related: {
                system: this.#system,
                correlation: {
                  parentField: secondRelation.sourceField,
                  childField: secondRelation.destField
                },
                subquery: addPrimaryKeysToAst(
                  this.#schema.tables[destSchema],
                  queryToDest.#ast
                )
              },
              op: "EXISTS"
            }
          }
        },
        op: "EXISTS"
      };
    }
    throw new Error(`Invalid relationship ${relationship}`);
  };
  #completedAST;
  _completeAst() {
    if (!this.#completedAST) {
      const finalOrderBy = addPrimaryKeys(
        this.#schema.tables[this.#tableName],
        this.#ast.orderBy
      );
      if (this.#ast.start) {
        const { row } = this.#ast.start;
        const narrowedRow = {};
        for (const [field] of finalOrderBy) {
          narrowedRow[field] = row[field];
        }
        this.#completedAST = {
          ...this.#ast,
          start: {
            ...this.#ast.start,
            row: narrowedRow
          },
          orderBy: finalOrderBy
        };
      } else {
        this.#completedAST = {
          ...this.#ast,
          orderBy: addPrimaryKeys(
            this.#schema.tables[this.#tableName],
            this.#ast.orderBy
          )
        };
      }
    }
    return this.#completedAST;
  }
  then(onFulfilled, onRejected) {
    return this.run().then(onFulfilled, onRejected);
  }
};
var completedAstSymbol = Symbol();
var QueryImpl = class _QueryImpl extends AbstractQuery {
  #delegate;
  #system;
  constructor(delegate, schema, tableName, ast, format, system = "client") {
    super(schema, tableName, ast, format, system);
    this.#system = system;
    this.#delegate = delegate;
  }
  get [completedAstSymbol]() {
    return this._completeAst();
  }
  [newQuerySymbol](schema, tableName, ast, format) {
    return new _QueryImpl(
      this.#delegate,
      schema,
      tableName,
      ast,
      format,
      this.#system
    );
  }
  materialize(factoryOrTTL, ttl = DEFAULT_TTL) {
    const t0 = Date.now();
    let factory;
    if (typeof factoryOrTTL === "function") {
      factory = factoryOrTTL;
    } else {
      ttl = factoryOrTTL ?? DEFAULT_TTL;
    }
    const ast = this._completeAst();
    const queryCompleteResolver = resolver();
    let queryComplete = this.#delegate.defaultQueryComplete;
    const removeServerQuery = this.#delegate.addServerQuery(ast, ttl, (got) => {
      if (got) {
        const t1 = Date.now();
        this.#delegate.onQueryMaterialized(this.hash(), ast, t1 - t0);
        queryComplete = true;
        queryCompleteResolver.resolve(true);
      }
    });
    const updateTTL = (newTTL) => {
      this.#delegate.updateServerQuery(ast, newTTL);
    };
    const input = buildPipeline(ast, this.#delegate);
    let removeCommitObserver;
    const onDestroy = () => {
      input.destroy();
      removeCommitObserver?.();
      removeServerQuery();
    };
    const view = this.#delegate.batchViewUpdates(
      () => (factory ?? arrayViewFactory)(
        this,
        input,
        this.format,
        onDestroy,
        (cb) => {
          removeCommitObserver = this.#delegate.onTransactionCommit(cb);
        },
        queryComplete || queryCompleteResolver.promise,
        updateTTL
      )
    );
    return view;
  }
  run(options) {
    this.#delegate.assertValidRunOptions(options);
    const v2 = this.materialize();
    if (options?.type === "complete") {
      return new Promise((resolve) => {
        v2.addListener((data, type) => {
          if (type === "complete") {
            v2.destroy();
            resolve(data);
          }
        });
      });
    }
    options?.type;
    const ret = v2.data;
    v2.destroy();
    return Promise.resolve(ret);
  }
  preload(options) {
    const { resolve, promise: complete } = resolver();
    const ast = this._completeAst();
    const unsub = this.#delegate.addServerQuery(
      ast,
      options?.ttl ?? DEFAULT_TTL,
      (got) => {
        if (got) {
          resolve();
        }
      }
    );
    return {
      cleanup: unsub,
      complete
    };
  }
};
function addPrimaryKeys(schema, orderBy) {
  orderBy = orderBy ?? [];
  const { primaryKey } = schema;
  const primaryKeysToAdd = new Set(primaryKey);
  for (const [field] of orderBy) {
    primaryKeysToAdd.delete(field);
  }
  if (primaryKeysToAdd.size === 0) {
    return orderBy;
  }
  return [
    ...orderBy,
    ...[...primaryKeysToAdd].map((key) => [key, "asc"])
  ];
}
function addPrimaryKeysToAst(schema, ast) {
  return {
    ...ast,
    orderBy: addPrimaryKeys(schema, ast.orderBy)
  };
}
function arrayViewFactory(_query, input, format, onDestroy, onTransactionCommit, queryComplete, updateTTL) {
  const v2 = new ArrayView(
    input,
    format,
    queryComplete,
    updateTTL
  );
  v2.onDestroy = onDestroy;
  onTransactionCommit(() => {
    v2.flush();
  });
  return v2;
}
function isCompoundKey(field) {
  return Array.isArray(field) && field.length >= 1;
}

export {
  isProd,
  hasOwn,
  deepEqual,
  assertJSONValue,
  assertJSONObject,
  parse,
  assert2 as assert,
  test,
  readonly,
  readonlyObject,
  readonlyArray,
  readonlyRecord,
  literalUnion,
  valita_exports,
  jsonSchema,
  jsonObjectSchema,
  h64,
  h128,
  rowSchema,
  toStaticParam,
  astSchema,
  normalizeAST,
  mapAST,
  mapCondition,
  hashOfAST,
  filterPush,
  ExpressionBuilder,
  createPredicate,
  transformFilters,
  assertOrderingIncludesPK,
  DEFAULT_TTL,
  parseTTL,
  compareTTL,
  normalizeTTL,
  newQuery,
  staticParam,
  SUBQ_PREFIX,
  defaultFormat,
  newQuerySymbol,
  AbstractQuery
};
//# sourceMappingURL=chunk-MIYN75UK.js.map
